// src/components/AdminMigrationTools.js

import { useState } from "react";

import {
  doc,
  getDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import {
  isOwnerAdmin,
} from "../security/permissions";

import auditOperationalAccessProfiles from "../utils/migrations/auditOperationalAccessProfiles";

import repairSafeOperationalAccessProfiles from "../utils/migrations/repairSafeOperationalAccessProfiles";

/*
 * =========================================================
 * LEGACY CREDENTIAL MAP CLEANUP
 * =========================================================
 */

export async function hardDeleteLegacyCredentialsMap(
  uid
) {
  const ref = doc(
    db,
    "users",
    uid
  );

  const snap =
    await getDoc(ref);

  if (!snap.exists()) {
    return {
      ok: false,
      uid,
      reason:
        "users doc missing",
    };
  }

  const data =
    snap.data() || {};

  const creds =
    data.credentials;

  const isMap =
    creds &&
    typeof creds === "object" &&
    !Array.isArray(creds);

  if (!isMap) {
    return {
      ok: true,
      uid,
      changed: false,
      reason:
        "credentials not a map",
    };
  }

  await updateDoc(
    ref,
    {
      credentials:
        deleteField(),

      updatedAt:
        serverTimestamp(),
    }
  );

  return {
    ok: true,
    uid,
    changed: true,
    reason:
      "deleted legacy credentials map",
  };
}

/*
 * =========================================================
 * ADMIN MIGRATION TOOLS
 * =========================================================
 */

export default function AdminMigrationTools({
  profile = {},
}) {
  /*
   * -------------------------------------------------------
   * LEGACY CREDENTIAL MIGRATION STATE
   * -------------------------------------------------------
   */

  const [
    uidsText,
    setUidsText,
  ] = useState("");

  const [
    out,
    setOut,
  ] = useState([]);

  /*
   * -------------------------------------------------------
   * OPERATIONAL ACCESS AUDIT STATE
   * -------------------------------------------------------
   */

  const [
    auditLoading,
    setAuditLoading,
  ] = useState(false);

  const [
    auditReport,
    setAuditReport,
  ] = useState(null);

  const [
    auditError,
    setAuditError,
  ] = useState("");

  /*
   * -------------------------------------------------------
   * SAFE ACCESS REPAIR STATE
   * -------------------------------------------------------
   */

  const [
    repairLoading,
    setRepairLoading,
  ] = useState(false);

  const [
    repairReport,
    setRepairReport,
  ] = useState(null);

  const [
    repairError,
    setRepairError,
  ] = useState("");

  /*
   * Canonical authorization check.
   *
   * Rendering this component inside the Admin dashboard
   * does NOT itself authorize a destructive migration.
   */
  const ownerAdmin =
    isOwnerAdmin(profile);

  const safeRepairCount =
    Number(
      auditReport
        ?.summary
        ?.byDisposition
        ?.SAFE_AUTO_REPAIR ||
        0
    );

  /*
   * =======================================================
   * READ-ONLY ACCESS AUDIT
   * =======================================================
   */

  const runOperationalAccessAudit =
    async () => {
      setAuditLoading(true);
      setAuditError("");
      setAuditReport(null);

      /*
       * A new audit invalidates any previously displayed
       * repair report.
       */
      setRepairReport(null);
      setRepairError("");

      try {
        const report =
          await auditOperationalAccessProfiles({ tenantId: profile?.tenantId });

        setAuditReport(
          report
        );

        console.log(
          "ADMIN MIGRATION TOOLS — OPERATIONAL ACCESS AUDIT RESULT",
          report
        );
      } catch (error) {
        console.error(
          "ADMIN MIGRATION TOOLS — OPERATIONAL ACCESS AUDIT FAILED",
          error
        );

        setAuditError(
          error?.message ||
            "Operational access audit failed."
        );
      } finally {
        setAuditLoading(false);
      }
    };

  /*
   * =======================================================
   * SAFE OPERATIONAL ACCESS REPAIR
   * =======================================================
   */

  const runSafeOperationalAccessRepair =
    async () => {
      setRepairError("");
      setRepairReport(null);

      /*
       * UI-level owner protection.
       *
       * Firestore rules remain the final authority,
       * but we should never even start this migration
       * from a non-owner UI session.
       */
      if (!ownerAdmin) {
        setRepairError(
          "Only Owner Admin may run the operational access repair."
        );

        return;
      }

      if (!auditReport) {
        setRepairError(
          "Run the read-only access audit first."
        );

        return;
      }

      if (
        safeRepairCount <= 0
      ) {
        setRepairError(
          "The current audit contains no SAFE_AUTO_REPAIR accounts."
        );

        return;
      }

      const confirmed =
        window.confirm(
          [
            "SHIFTBRIDGE OPERATIONAL ACCESS REPAIR",
            "",
            `The latest audit identified ${safeRepairCount} SAFE_AUTO_REPAIR account(s).`,
            "",
            "This operation may:",
            "• create missing accessProfiles",
            "• replace stale permission maps",
            "• normalize access-profile schema",
            "• repair facility scope",
            "• rebuild Planner/Worker roster membership",
            "",
            "Blocked, duplicate-identity, and not-ready accounts will NOT be repaired.",
            "",
            "The repair engine will run a fresh audit again before writing.",
            "",
            "Continue?",
          ].join("\n")
        );

      if (!confirmed) {
        return;
      }

      setRepairLoading(true);

      try {
        const report =
          await repairSafeOperationalAccessProfiles({ tenantId: profile?.tenantId });

        setRepairReport(
          report
        );

        console.log(
          "ADMIN MIGRATION TOOLS — SAFE OPERATIONAL ACCESS REPAIR RESULT",
          report
        );

        /*
         * The repair function already runs its own
         * post-repair audit.
         *
         * Clear the old detailed audit so the UI does
         * not continue presenting stale rows.
         */
        setAuditReport(null);
      } catch (error) {
        console.error(
          "ADMIN MIGRATION TOOLS — SAFE OPERATIONAL ACCESS REPAIR FAILED",
          error
        );

        setRepairError(
          error?.message ||
            "Safe operational access repair failed."
        );
      } finally {
        setRepairLoading(false);
      }
    };

  /*
   * =======================================================
   * LEGACY CREDENTIAL MAP MIGRATION
   * =======================================================
   */

  const run =
    async () => {
      const uids =
        String(
          uidsText || ""
        )
          .split(
            /[\s,\n]+/
          )
          .map(
            (value) =>
              String(
                value || ""
              ).trim()
          )
          .filter(Boolean);

      if (
        uids.length === 0
      ) {
        setOut([
          {
            ok: false,
            reason:
              "Enter at least one uid.",
          },
        ]);

        return;
      }

      const results = [];

      for (
        const uid of uids
      ) {
        try {
          const result =
            await hardDeleteLegacyCredentialsMap(
              uid
            );

          results.push(
            result
          );
        } catch (error) {
          results.push({
            ok: false,
            uid,
            reason:
              error?.message ||
              String(error),
          });
        }
      }

      setOut(
        results
      );
    };

  /*
   * =======================================================
   * UI
   * =======================================================
   */

  return (
    <div
      style={{
        display: "grid",
        gap: 14,
      }}
    >
      {/* =================================================
          OPERATIONAL ACCESS AUDIT / REPAIR
          ================================================= */}

      <div
        style={{
          border:
            "1px solid #dbe4ee",
          borderRadius: 16,
          background: "#ffffff",
          padding: 18,
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 950,
            color: "#0f172a",
          }}
        >
          Operational Access Audit
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#64748b",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          Validate users, access profiles,
          facility scope, permissions, and
          facility roster membership before
          making authorization changes.
        </div>

        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={
              runOperationalAccessAudit
            }
            disabled={
              auditLoading ||
              repairLoading
            }
            style={{
              border:
                "1px solid #2563eb",
              background:
                auditLoading
                  ? "#bfdbfe"
                  : "#2563eb",
              color: "#ffffff",
              borderRadius: 10,
              padding:
                "10px 14px",
              fontSize: 12,
              fontWeight: 900,
              cursor:
                auditLoading ||
                repairLoading
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {auditLoading
              ? "Running Audit..."
              : "Run Read-Only Access Audit"}
          </button>

          {ownerAdmin ? (
            <button
              type="button"
              onClick={
                runSafeOperationalAccessRepair
              }
              disabled={
                repairLoading ||
                auditLoading ||
                !auditReport ||
                safeRepairCount <=
                  0
              }
              style={{
                border:
                  "1px solid #b45309",
                background:
                  repairLoading
                    ? "#fde68a"
                    : !auditReport ||
                      safeRepairCount <=
                        0
                    ? "#f1f5f9"
                    : "#f59e0b",
                color:
                  !auditReport ||
                  safeRepairCount <=
                    0
                    ? "#64748b"
                    : "#422006",
                borderRadius: 10,
                padding:
                  "10px 14px",
                fontSize: 12,
                fontWeight: 950,
                cursor:
                  repairLoading ||
                  auditLoading ||
                  !auditReport ||
                  safeRepairCount <=
                    0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {repairLoading
                ? "Repairing..."
                : `Repair Safe Accounts (${safeRepairCount})`}
            </button>
          ) : null}
        </div>

        {!ownerAdmin ? (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 10,
              background: "#f8fafc",
              border:
                "1px solid #e2e8f0",
              color: "#64748b",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Repair operations are restricted
            to Owner Admin.
          </div>
        ) : null}

        {auditError ? (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 10,
              background: "#fef2f2",
              border:
                "1px solid #fecaca",
              color: "#b91c1c",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {auditError}
          </div>
        ) : null}

        {repairError ? (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 10,
              background: "#fef2f2",
              border:
                "1px solid #fecaca",
              color: "#b91c1c",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {repairError}
          </div>
        ) : null}

        {auditReport?.summary ? (
          <>
            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(130px, 1fr))",
                gap: 8,
              }}
            >
              <SummaryBox
                label="Total"
                value={
                  auditReport
                    .summary
                    .total
                }
              />

              <SummaryBox
                label="Canonical"
                value={
                  auditReport
                    .summary
                    .canonical
                }
              />

              <SummaryBox
                label="Needs Attention"
                value={
                  auditReport
                    .summary
                    .needsAttention
                }
              />

              <SummaryBox
                label="Safe Repair"
                value={
                  safeRepairCount
                }
              />

              <SummaryBox
                label="Blocked"
                value={
                  auditReport
                    .summary
                    .byDisposition
                    ?.BLOCKED_SOURCE_DATA ||
                  0
                }
              />

              <SummaryBox
                label="Identity Review"
                value={
                  auditReport
                    .summary
                    .byDisposition
                    ?.MANUAL_IDENTITY_REVIEW ||
                  0
                }
              />

              <SummaryBox
                label="Not Ready"
                value={
                  auditReport
                    .summary
                    .byDisposition
                    ?.NOT_OPERATIONALLY_READY ||
                  0
                }
              />
            </div>

            <pre
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                background: "#0f172a",
                color: "#e2e8f0",
                overflowX: "auto",
                maxHeight: 420,
                fontSize: 11,
                lineHeight: 1.45,
              }}
            >
              {JSON.stringify(
                auditReport,
                null,
                2
              )}
            </pre>
          </>
        ) : null}

        {repairReport ? (
          <div
            style={{
              marginTop: 14,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 950,
                color: "#0f172a",
              }}
            >
              Safe Repair Result
            </div>

            <div
              style={{
                marginTop: 8,
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(130px, 1fr))",
                gap: 8,
              }}
            >
              <SummaryBox
                label="Attempted"
                value={
                  repairReport
                    ?.attempted ||
                  0
                }
              />

              <SummaryBox
                label="Repaired"
                value={
                  repairReport
                    ?.repaired ||
                  0
                }
              />

              <SummaryBox
                label="Failed"
                value={
                  repairReport
                    ?.failed ||
                  0
                }
              />

              <SummaryBox
                label="Canonical After"
                value={
                  repairReport
                    ?.after
                    ?.canonical ||
                  0
                }
              />

              <SummaryBox
                label="Attention After"
                value={
                  repairReport
                    ?.after
                    ?.needsAttention ||
                  0
                }
              />
            </div>

            <pre
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 12,
                background: "#0f172a",
                color: "#e2e8f0",
                overflowX: "auto",
                maxHeight: 420,
                fontSize: 11,
                lineHeight: 1.45,
              }}
            >
              {JSON.stringify(
                repairReport,
                null,
                2
              )}
            </pre>
          </div>
        ) : null}
      </div>

      {/* =================================================
          LEGACY CREDENTIAL TOOL
          ================================================= */}

      <div
        style={{
          border:
            "1px solid #e2e8f0",
          borderRadius: 16,
          background: "#ffffff",
          padding: 18,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 950,
            color: "#0f172a",
          }}
        >
          Migration: delete legacy
          credentials MAP
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          Enter one or more worker UIDs.
          This tool only removes the old
          object-map form of the credentials
          field.
        </div>

        <textarea
          value={uidsText}
          onChange={(event) =>
            setUidsText(
              event.target.value
            )
          }
          placeholder={
            "uid1\nuid2\nuid3"
          }
          rows={5}
          style={{
            marginTop: 12,
            width: "100%",
            boxSizing:
              "border-box",
            border:
              "1px solid #cbd5e1",
            borderRadius: 10,
            padding: 10,
            resize: "vertical",
            fontFamily:
              "monospace",
          }}
        />

        <button
          type="button"
          onClick={run}
          style={{
            marginTop: 10,
            border:
              "1px solid #dc2626",
            background: "#ffffff",
            color: "#b91c1c",
            borderRadius: 10,
            padding:
              "9px 12px",
            fontSize: 12,
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Run migration
        </button>

        {out.length > 0 ? (
          <pre
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background: "#f8fafc",
              border:
                "1px solid #e2e8f0",
              overflowX: "auto",
              fontSize: 11,
            }}
          >
            {JSON.stringify(
              out,
              null,
              2
            )}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

/*
 * =========================================================
 * SMALL SUMMARY CARD
 * =========================================================
 */

function SummaryBox({
  label,
  value,
}) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 10,
        background: "#f8fafc",
        border:
          "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform:
            "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 3,
          color: "#0f172a",
          fontSize: 20,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}